import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { STATUS_COLORS, STATUS_LABELS } from "@/components/Avatar";
import { Circle, Clock, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Verbal, Focus-timer-aware status picker.
 *
 * Presents:
 *  - Primary presets (Available / Busy / Away)
 *  - Focus mode with a timer (25 / 50 / 90 minutes)
 *  - Optional 40-char custom message
 *  - Clear-status shortcut when a status_text is currently set
 *
 * Server POST /api/presence accepts { status, status_text, focus_minutes }.
 * The server derives `focus_until` and auto-expires it via _compute_effective.
 */
export default function StatusPickerRich({
  currentStatus = "available",
  statusText = null,
  focusUntil = null,
  onOptimistic, // (patch) => void — parent can bump local state
  children, // trigger button
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const post = async (payload, label) => {
    setBusy(true);
    try {
      await api.post("/presence", payload);
      onOptimistic?.(payload);
      toast.success(label || "Status updated");
      setOpen(false);
    } catch (e) {
      toast.error("Could not update status");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = (status) => post({ status, status_text: "__clear__" }, `Status: ${STATUS_LABELS[status] || status}`);
  const setFocus = (mins) => post({ status: "focus", focus_minutes: mins }, `Focus mode · ${mins}m`);
  const clearText = () => post({ status_text: "__clear__", focus_until: "__clear__" }, "Status cleared");

  const remainingMin = (() => {
    if (!focusUntil) return null;
    try {
      const ms = new Date(focusUntil).getTime() - Date.now();
      return ms > 0 ? Math.max(1, Math.ceil(ms / 60000)) : null;
    } catch { return null; }
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" data-testid="status-picker-panel">
        {/* Current line */}
        <div className="px-2 pb-2 border-b border-zinc-100">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Currently</div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[currentStatus] }} />
            <span className="font-medium">{STATUS_LABELS[currentStatus] || "Available"}</span>
            {remainingMin != null && (
              <span className="text-xs text-zinc-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {remainingMin}m left
              </span>
            )}
          </div>
          {statusText && (
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="text-xs text-zinc-600 truncate" data-testid="status-picker-current-text">{statusText}</span>
              <button
                type="button"
                onClick={clearText}
                disabled={busy}
                aria-label="Clear status message"
                data-testid="status-picker-clear-text"
                className="text-[10px] text-zinc-400 hover:text-zinc-700 flex items-center gap-0.5"
              ><X className="w-3 h-3"/> Clear</button>
            </div>
          )}
        </div>

        {/* Primary statuses */}
        <div className="pt-1 pb-1">
          {[
            { id: "available", label: "Available" },
            { id: "busy", label: "Busy" },
            { id: "away", label: "Away" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              disabled={busy}
              data-testid={`status-${s.id}`}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-zinc-100 text-sm flex items-center gap-2 disabled:opacity-50"
            >
              <Circle className="w-2 h-2 fill-current" style={{ color: STATUS_COLORS[s.id] }} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Focus timer */}
        <div className="border-t border-zinc-100 pt-1 pb-1">
          <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            <Clock className="w-3 h-3"/> Focus mode
          </div>
          <div className="flex gap-1 px-1">
            {[25, 50, 90].map((mins) => (
              <button
                key={mins}
                onClick={() => setFocus(mins)}
                disabled={busy}
                data-testid={`status-focus-${mins}`}
                className="flex-1 py-1.5 rounded text-xs font-medium bg-zinc-50 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
              >{mins}m</button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
